
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Zap, Globe, Trophy, Heart, ShoppingCart, UserCircle, Sparkles, Rocket } from 'lucide-react';
import { AppView, AuthView } from '../types.ts';

interface NavigationProps {
  activeView: AppView;
  setActiveView: (view: AppView) => void;
  setAuthView: (view: AuthView) => void;
  isGuest: boolean;
  avatarUrl: string | null;
  isBgEnabled: boolean;
  setIsBgEnabled: (val: boolean) => void;
  dailyUsage: number;
  bonusBlasts: number;
  dailyLimit: number;
}

const VaultDoorIcon = ({ size = 18, className = "" }: { size?: number, className?: string }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2.5" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="12" cy="5" r="0.5" fill="currentColor" />
    <circle cx="12" cy="19" r="0.5" fill="currentColor" />
    <circle cx="5" cy="12" r="0.5" fill="currentColor" />
    <circle cx="19" cy="12" r="0.5" fill="currentColor" />
    <circle cx="7.05" cy="7.05" r="0.5" fill="currentColor" />
    <circle cx="16.95" cy="16.95" r="0.5" fill="currentColor" />
    <circle cx="7.05" cy="16.95" r="0.5" fill="currentColor" />
    <circle cx="16.95" cy="7.05" r="0.5" fill="currentColor" />
    <path d="M12 8v2" />
    <path d="M12 14v2" />
    <path d="M8 12h2" />
    <path d="M14 12h6" />
  </svg>
);

export default function Navigation({ 
  activeView, setActiveView, setAuthView, isGuest, avatarUrl, isBgEnabled, setIsBgEnabled,
  dailyUsage, bonusBlasts, dailyLimit
}: NavigationProps) {
  const location = useLocation();
  const isLaunchpad = location.pathname.startsWith('/launchpad');
  
  // Total Energy logic for UI display
  const dailyRemaining = Math.max(0, dailyLimit - dailyUsage);
  const energyTotal = dailyRemaining + bonusBlasts;
  
  // Percent should represent fill relative to the DAILY limit (can exceed 100% with bonus)
  const energyPercent = Math.min(100, (energyTotal / dailyLimit) * 100);

  return (
    <nav className="w-full bg-black/80 backdrop-blur-lg border-b-4 border-yellow-400 p-4">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => setActiveView('editor')} aria-label="Brainrot Blast Home">
          <Zap className="text-yellow-400 fill-yellow-400" size={32} />
          <h1 className="text-xl font-bungee text-yellow-400 hidden lg:block uppercase">Brainrot Blast!</h1>
        </div>
        
        <div className="flex items-center gap-1 md:gap-3">
          <button onClick={() => setActiveView('toprot')} className={`px-3 py-2 rounded-xl flex items-center gap-2 transition-all ${activeView === 'toprot' && !isLaunchpad ? 'bg-yellow-400 text-black' : 'bg-zinc-900 text-zinc-400'}`} aria-label="View Top Rots">
            <Globe size={18}/><span className="hidden sm:inline font-bungee text-[10px] uppercase">Top Rot</span>
          </button>
          <button onClick={() => setActiveView('leaderboard')} className={`px-3 py-2 rounded-xl flex items-center gap-2 transition-all ${activeView === 'leaderboard' && !isLaunchpad ? 'bg-purple-600 text-white' : 'bg-zinc-900 text-zinc-400'}`} aria-label="View Global Leaderboard">
            <Trophy size={18}/><span className="hidden sm:inline font-bungee text-[10px] uppercase">Top Aura</span>
          </button>
          <button onClick={() => setActiveView('vibecheck')} className={`px-3 py-2 rounded-xl flex items-center gap-2 transition-all ${activeView === 'vibecheck' && !isLaunchpad ? 'bg-red-600 text-white' : 'bg-zinc-900 text-zinc-400'}`} aria-label="Rate Memes in Vibe Check">
            <Heart size={18}/><span className="hidden sm:inline font-bungee text-[10px] uppercase">Vibe Check</span>
          </button>
          <button onClick={() => setActiveView('history')} className={`px-3 py-2 rounded-xl flex items-center gap-2 transition-all ${activeView === 'history' && !isLaunchpad ? 'bg-blue-600 text-white' : 'bg-zinc-900 text-zinc-400'}`} aria-label="View Your Vault">
            <VaultDoorIcon size={18} /><span className="hidden sm:inline font-bungee text-[10px] uppercase">Vault</span>
          </button>
          <Link to="/launchpad" className={`px-3 py-2 rounded-xl flex items-center gap-2 transition-all relative group ${isLaunchpad ? 'bg-emerald-600 text-white shadow-[0_0_15px_rgba(16,185,129,0.4)]' : 'bg-zinc-900 text-zinc-400 hover:text-emerald-400'}`} aria-label="Visit Launchpad Labs">
            <Rocket size={18} className={isLaunchpad ? 'animate-bounce' : ''} />
            <span className="hidden sm:inline font-bungee text-[10px] uppercase">Launchpad</span>
            <span className="absolute -top-2 -right-2 bg-emerald-500 text-[8px] font-black px-1.5 py-0.5 rounded-full text-black border border-black animate-pulse">LABS</span>
          </Link>
        </div>

        <div className="flex items-center gap-3">
          {/* ENERGY INDICATOR */}
          <div className="hidden md:flex flex-col items-end gap-1 px-3 border-r border-zinc-800">
            <div className="flex items-center gap-1.5">
              {bonusBlasts > 0 && <span title="Bonus Blasts Active"><Sparkles size={10} className="text-blue-400 animate-pulse" /></span>}
              <Zap size={10} className={`${energyTotal <= 2 ? 'text-red-500 fill-red-500 animate-pulse' : (bonusBlasts > 0 ? 'text-blue-400 fill-blue-400' : 'text-yellow-400 fill-yellow-400')}`} />
              <span className={`text-[8px] font-black uppercase tracking-widest ${bonusBlasts > 0 ? 'text-blue-400' : 'text-zinc-500'}`}>
                Energy: {energyTotal}/{dailyLimit}
              </span>
            </div>
            <div className="w-24 h-2 bg-zinc-950 rounded-full border border-zinc-900 overflow-hidden relative shadow-inner">
              <div 
                className={`h-full transition-all duration-700 ease-out ${bonusBlasts > 0 ? 'bg-blue-400 shadow-[0_0_8px_rgba(96,165,250,0.5)]' : (energyTotal <= 2 ? 'bg-red-600' : 'bg-yellow-400')}`} 
                style={{ width: `${energyPercent}%` }} 
              />
            </div>
          </div>

          <button 
            onClick={() => setIsBgEnabled(!isBgEnabled)} 
            className={`p-2.5 rounded-xl border-b-4 transition-all ${isBgEnabled ? 'bg-indigo-600 border-indigo-900 text-white shadow-[0_0_15px_rgba(79,70,229,0.4)]' : 'bg-zinc-900 border-zinc-950 text-zinc-500'}`}
            title="Toggle Stars"
            aria-label="Toggle Starfield Background"
          >
            <Sparkles size={20} />
          </button>
          <button onClick={() => setActiveView('store')} className={`p-2.5 rounded-xl border-b-4 transition-all ${activeView === 'store' ? 'bg-zinc-100 text-black border-zinc-400' : 'bg-zinc-900 border-zinc-950 text-zinc-400'}`} aria-label="Open Store">
            <ShoppingCart size={20} />
          </button>
          <button onClick={() => isGuest ? setAuthView('login') : setActiveView('account')} className="relative rounded-xl border-2 border-zinc-800 overflow-hidden active:scale-95 transition-transform shadow-lg" aria-label="User Account">
            {avatarUrl ? <img src={avatarUrl} className="w-10 h-10 object-cover" alt="User Avatar" /> : <div className="p-2.5 bg-zinc-900 text-zinc-400"><UserCircle size={20} /></div>}
          </button>
        </div>
      </div>
    </nav>
  );
}
